import type { Connection, Edge, Node } from '@xyflow/react'
import { addEdge, reconnectEdge } from '@xyflow/react'
import { dropOtherUniverseEdges } from '@/features/backtest/utils/single-universe'
import {
  applyCollapseState,
  getUnderlyingEdgeIds,
  isProxyEdge,
} from '@/features/backtest/utils/collapse-model'
import {
  resolveBoundaryConnection,
  resolveBoundaryReconnect,
} from '@/features/backtest/utils/subnet-boundary-ops'
import {
  isPassthroughType,
  resolvePassthroughDownstream,
  resolvePassthroughUpstream,
  resolveValidationSource,
  type PassthroughConsumer,
} from '@/features/backtest/utils/inline-passthroughs'
import {
  conflictingConsumer,
  consumerName,
  passthroughName,
  realConsumers,
} from '@/features/backtest/utils/subnet-input-needs'
import { validateNodeConnection } from '@/features/backtest/utils/universal-node-validation'
import { UniversalNodeRegistry } from '@/features/backtest/schemas/universal-node-schema'
import { getConnectionPillDisplay } from '@/features/backtest/components/nodes/node-utils'
import {
  LOGIC_NODES,
  MATH_NODES,
  STRATEGY_NODES,
  TECHNICAL_NODES,
} from '@/features/backtest/constants/node-type-groups'
import type { NodeInput } from '@/features/backtest/types/nodes'
import { inputsForTypeSwitch } from '@/features/backtest/components/nodes/universal-node-helpers'
import { resolveFace } from '@/features/backtest/schemas/node-face'
import {
  deadSocketReason,
  inputsReleasedBy,
  isDeadSocket,
  isInputActive,
  isKeyedTo,
  isRetiredSocket,
  retiredSocketReason,
} from '@/features/backtest/schemas/input-activity'
import {
  getExposure,
  removeExposedParam,
  type SubnetExposure,
} from '@/features/backtest/utils/subnet-exposure'

/**
 * Wire commands for the backtest editor (connection-logic audit 2026-10-06,
 * step 5). Every way a user adds, moves or removes a wire asks these, so the
 * rules live once: the check, replacing the wire already on an input, the
 * one-universe release, the `inputs[]` mirror, the validation stamp and the
 * collapsed-subnet projection. Pure over `{ nodes, edges }`: no React, no
 * stores. A refusal comes back as a reason for the caller to show; the caller
 * takes the undo snapshot and sets state.
 *
 * Nodes nothing changed come back as the same objects (the editor's render
 * cache keys on them).
 */

export interface Graph {
  nodes: Node[]
  edges: Edge[]
}

export type Verdict = { ok: true } | { ok: false; reason: string }

export type PlanResult =
  | { ok: true; changed: boolean; nodes: Node[]; edges: Edge[] }
  | { ok: false; reason: string }

export interface WireEnds {
  source: string
  sourceHandle?: string | null
  target: string
  targetHandle?: string | null
}

const REFUSED = 'This connection is not allowed.'
const fail = (reason: string): PlanResult => ({ ok: false, reason })
const unchanged = (graph: Graph): PlanResult => ({ ok: true, changed: false, ...graph })
const isCanonical = (edge: Edge) => !isProxyEdge(edge)
const onSocket = (edge: Edge, target: string, targetHandle: string | null | undefined) =>
  edge.target === target && (edge.targetHandle ?? null) === (targetHandle ?? null)

// ── The `inputs[]` mirror ────────────────────────────────────────────────────

// Valid OHLC field choices when the upstream node is priceData/marketData.
// Used to preserve the user's prior field selection on edge re-evaluation
// rather than slamming everyone back to 'close' on every connect.
const OHLC_FIELDS = new Set(['open', 'high', 'low', 'close', 'volume'])
const preserveOhlcField = (existing: unknown): string =>
  typeof existing === 'string' && OHLC_FIELDS.has(existing) ? existing : 'close'

/**
 * Resolve the BARE `source` field an input should store for an incoming edge:
 * always a plain field/output id (`close`, `sma`, `result`), NEVER a composite
 * pill label.
 *
 * Why this matters: the OHLCV label resolver folds `input.source` back in as the
 * field name (`${capitalize(source)}-${timeframe}`). Storing a full pill label
 * like `Close-1m` makes it re-fold on the next connect → `Close-1m-1m`, then
 * `Close-1m-1m-1m`, compounding the timeframe on every reconnect.
 *
 * The trap: a subnet "in" (`subnetInput`) is a pass-through, so branching on the
 * IMMEDIATE source type skipped the priceData branch (which correctly stores a
 * bare field) and hit the pill-label fallback. Resolving through pass-throughs
 * to the effective upstream treats a subnet input fed by priceData AS priceData.
 * `preserveOhlcField` also self-heals already-corrupted values (a non-OHLC
 * string resets to `close`).
 */
export function resolveInputSourceValue(
  srcNode: Node,
  edge: Edge,
  existingSource: unknown,
  targetNodeId: string,
  targetHandle: string,
  currentNodes: Node[],
  currentEdges: Edge[],
): string {
  let effectiveType = srcNode.type ?? ''
  let effectiveHandle = edge.sourceHandle
  if (isPassthroughType(srcNode.type)) {
    const upstream = resolvePassthroughUpstream(srcNode.id, currentNodes, currentEdges)
    if (upstream) {
      effectiveType = upstream.type
      effectiveHandle = upstream.sourceHandle
    }
  }

  if (effectiveType === 'priceData' || effectiveType === 'marketData') {
    return preserveOhlcField(existingSource)
  }

  const srcSchema = UniversalNodeRegistry.getSchema(effectiveType)
  if (srcSchema?.outputs && srcSchema.outputs.length > 0) {
    // Respect the output handle the edge was actually dragged from. Defaulting
    // to outputs[0] corrupted same-node dual-output wiring (percK/percD both
    // serialized percK).
    const dragged = srcSchema.outputs.find((o) => o.id === effectiveHandle)
    return (dragged ?? srcSchema.outputs[0]).id
  }

  // Fallback for genuinely non-universal sources.
  return getConnectionPillDisplay(
    srcNode.id,
    currentNodes,
    currentEdges,
    targetNodeId,
    targetHandle,
  )
}

/**
 * Node types whose `inputs[]` keep a `source` for each wire. Connecting and
 * disconnecting use this one list; until 2026-10-06 a disconnect skipped the
 * strategy nodes, so a Trade Manager socket kept the source of a wire it had
 * lost. (Which nodes carry the mirror at all is audit step 7.)
 */
const MIRRORED = new Set([...MATH_NODES, ...TECHNICAL_NODES, ...LOGIC_NODES, ...STRATEGY_NODES])

const inputsOf = (node: Node): NodeInput[] | null =>
  Array.isArray(node.data?.inputs) ? (node.data.inputs as NodeInput[]) : null

/** The node with its `inputs[]` mirror refreshed after it gained a wire. */
export function patchInputsOnConnect(node: Node, nodes: Node[], edges: Edge[]): Node {
  const type = node.type ?? ''
  if (!MIRRORED.has(type)) return node
  const inputs = [...(inputsOf(node) ?? [])]
  const incoming = edges.filter((e) => isCanonical(e) && e.target === node.id)

  for (const edge of incoming) {
    const srcNode = nodes.find((n) => n.id === edge.source)
    if (!srcNode) continue
    const handle = edge.targetHandle || 'input_1'
    const index = inputs.findIndex((input) => input.name === handle)
    const source = resolveInputSourceValue(
      srcNode,
      edge,
      index >= 0 ? inputs[index].source : undefined,
      node.id,
      handle,
      nodes,
      edges,
    )
    if (TECHNICAL_NODES.has(type)) {
      if (index >= 0) inputs[index] = { ...inputs[index], inputType: 'node', source }
    } else if (index >= 0 && inputs[index].inputType === 'node') {
      inputs[index] = {
        ...inputs[index],
        source,
        // An And/Or input is always explicitly on or off.
        ...(type === 'merge' ? { enabled: inputs[index].enabled !== false } : {}),
      }
    } else if (type === 'merge') {
      inputs.push({ name: handle, source, inputType: 'node', enabled: true })
    }
  }
  return { ...node, data: { ...node.data, inputs } }
}

/** The node with its `inputs[]` mirror refreshed after it lost a wire. */
export function patchInputsOnDisconnect(node: Node, nodes: Node[], edges: Edge[]): Node {
  const inputs = inputsOf(node)
  if (!MIRRORED.has(node.type ?? '') || !inputs) return node
  const next = inputs.map((input) => {
    if (input.inputType !== 'node') return input
    const wire = edges.find(
      (e) => isCanonical(e) && e.target === node.id && e.targetHandle === input.name,
    )
    if (!wire) {
      // Keep a chosen OHLC field through a transient disconnect: deleting a
      // reroute removes its wires a beat BEFORE the bypass wire reconnects to
      // Price Data, and nulling here would lose the field. `source` is
      // display-only without a wire (graph-to-recipe reads the field only
      // when a backing edge exists), so it never leaks into a run.
      if (typeof input.source === 'string' && OHLC_FIELDS.has(input.source)) return input
      return input.source == null ? input : { ...input, source: null }
    }
    // Still wired (a wire into another input went): refresh the source text,
    // resolving pass-throughs so a reroute before Price Data keeps its field.
    const immediate = nodes.find((n) => n.id === wire.source)
    let sourceNode = immediate
    if (immediate && isPassthroughType(immediate.type)) {
      const upstream = resolvePassthroughUpstream(immediate.id, nodes, edges)
      if (upstream) sourceNode = nodes.find((n) => n.id === upstream.id) ?? immediate
    }
    if (!sourceNode) return input
    const source =
      sourceNode.type === 'priceData' || sourceNode.type === 'marketData'
        ? preserveOhlcField(input.source)
        : getConnectionPillDisplay(sourceNode.id, nodes, edges, node.id, input.name)
    return source === input.source ? input : { ...input, source }
  })
  return next.every((input, i) => input === inputs[i])
    ? node
    : { ...node, data: { ...node.data, inputs: next } }
}

/** `nodes` with the mirror of each node in `targets` refreshed after wires went. */
export function patchNodesAfterDisconnect(
  nodes: Node[],
  edges: Edge[],
  targets: ReadonlySet<string>,
): Node[] {
  if (targets.size === 0) return nodes
  return nodes.map((node) =>
    targets.has(node.id) ? patchInputsOnDisconnect(node, nodes, edges) : node,
  )
}

/** The target after gaining a wire: its mirror, and no stale "missing input" stamp. */
function connectedTarget(node: Node, nodes: Node[], edges: Edge[]): Node {
  const patched = patchInputsOnConnect(node, nodes, edges)
  if (patched.data?.validationError === undefined) return patched
  return { ...patched, data: { ...patched.data, validationError: undefined } }
}

// ── The check ────────────────────────────────────────────────────────────────

/**
 * Whether a wire may be drawn, by the editor's one rule set
 * (universal-node-validation.ts). A pass-through (a reroute or a subnet pill)
 * hands one value to every node it feeds (subnet input needs, 2026-10-27):
 * - a wire into one is checked against each real node it feeds;
 * - a wire out of one is checked as whatever feeds it;
 * - one nothing feeds yet may only feed nodes that share a need, so whatever
 *   is wired in later can feed them all.
 * A Switcher keeps one kind of input.
 */
export function canConnect(graph: Graph, conn: WireEnds): Verdict {
  const { nodes, edges } = graph
  const sourceNode = nodes.find((n) => n.id === conn.source)
  const targetNode = nodes.find((n) => n.id === conn.target)
  if (!sourceNode || !targetNode) return { ok: false, reason: 'Source or target node not found.' }
  if (isPassthroughType(targetNode.type)) return canConnectInto(graph, sourceNode, conn, targetNode)
  // A socket its mode does not read: the wire would do nothing (input-activity).
  const targetSchema = UniversalNodeRegistry.getSchema(targetNode.type || '')
  if (
    targetSchema &&
    conn.targetHandle &&
    isDeadSocket(targetSchema, conn.targetHandle, targetNode.data?.parameters)
  )
    return { ok: false, reason: deadSocketReason(targetSchema, conn.targetHandle) }
  // A retired socket keeps a saved wire, but takes no new one (input-activity).
  if (
    targetSchema &&
    conn.targetHandle &&
    isRetiredSocket(targetSchema.inputs.find((input) => input.id === conn.targetHandle))
  )
    return { ok: false, reason: retiredSocketReason(targetSchema, conn.targetHandle) }
  const from = resolveValidationSource(sourceNode, conn.sourceHandle ?? null, nodes, edges)
  if (!from) {
    return canShareNeed(graph, sourceNode, [
      {
        nodeId: targetNode.id,
        handle: conn.targetHandle ?? '',
        type: targetNode.type ?? '',
        data: targetNode.data,
      },
    ])
  }
  const verdict = checkWire(graph, from, targetNode, conn.targetHandle, conn.source)
  return verdict.isValid ? { ok: true } : { ok: false, reason: verdict.errorMessage || REFUSED }
}

/** The wire rule for a real source into one input, a Switcher's other inputs included. */
function checkWire(
  graph: Graph,
  from: { type: string; handle: string },
  target: Node,
  targetHandle: string | null | undefined,
  source: string,
) {
  const { nodes, edges } = graph
  const existingConnections =
    target.type === 'switcher'
      ? edges
          .filter((e) => e.target === target.id && e.source !== source)
          .map((e) => ({
            sourceType: nodes.find((n) => n.id === e.source)?.type || '',
            targetHandle: e.targetHandle || '',
          }))
      : undefined
  return validateNodeConnection(
    from.type,
    target.type || '',
    targetHandle ?? undefined,
    from.handle,
    existingConnections,
    target.data,
  )
}

/**
 * Into a pass-through: the source reaches every real node it feeds, so each
 * must take it; the first that does not refuses the wire, named. A source
 * nothing feeds yet has nothing to check yet, but those nodes join what it
 * already feeds, so they must share a need.
 */
function canConnectInto(graph: Graph, sourceNode: Node, conn: WireEnds, target: Node): Verdict {
  const { nodes, edges } = graph
  const from = resolveValidationSource(sourceNode, conn.sourceHandle ?? null, nodes, edges)
  if (!from)
    return canShareNeed(graph, sourceNode, resolvePassthroughDownstream(target.id, nodes, edges))
  for (const consumer of realConsumers(resolvePassthroughDownstream(target.id, nodes, edges))) {
    const node = nodes.find((n) => n.id === consumer.nodeId)
    if (!node) continue
    const verdict = checkWire(graph, from, node, consumer.handle, target.id)
    if (!verdict.isValid) {
      return {
        ok: false,
        reason: `${passthroughName(target)} feeds ${consumerName(consumer)}: ${verdict.errorMessage || REFUSED}`,
      }
    }
  }
  return { ok: true }
}

/**
 * Out of a pass-through nothing feeds yet: the inputs the wire reaches
 * (`joining`) join the ones it already feeds, and they must share a need.
 * Behind a chain (a reroute after an unfed pill) the chain's head holds the
 * value, so it names the refusal and its consumers are the ones to share with.
 */
function canShareNeed(graph: Graph, sourceNode: Node, joining: PassthroughConsumer[]): Verdict {
  const { nodes, edges } = graph
  let head = sourceNode
  for (let hops = 0; hops < 50; hops++) {
    const incoming = edges.find((e) => isCanonical(e) && e.target === head.id)
    const upstream = incoming && nodes.find((n) => n.id === incoming.source)
    if (!upstream || !isPassthroughType(upstream.type)) break
    head = upstream
  }
  // The wire itself may be in the graph already (a setting change re-checks its wires).
  const joins = new Set(joining.map((c) => `${c.nodeId}:${c.handle}`))
  const others = resolvePassthroughDownstream(head.id, nodes, edges).filter(
    (c) => !joins.has(`${c.nodeId}:${c.handle}`),
  )
  const clash = conflictingConsumer(others, joining)
  if (!clash) return { ok: true }
  return {
    ok: false,
    reason: `${passthroughName(head)} already feeds ${consumerName(clash)}, which takes something else`,
  }
}

// ── Connect ──────────────────────────────────────────────────────────────────

/**
 * Add a wire: the check, the wire already on that input replaced, the
 * one-universe release, the target's mirror and stamp, then the projection
 * (a replaced wire may have been the one under a collapsed subnet's proxy).
 * A drop on a collapsed subnet's boundary is translated to the canonical wire
 * it stands for first (subnet-boundary-ops).
 */
export function planConnect(
  graph: Graph,
  conn: WireEnds,
  options: { edgeType?: string } = {},
): PlanResult {
  const { nodes, edges } = graph
  const plan = resolveBoundaryConnection(conn as Connection, nodes, edges)
  if (plan.kind === 'unresolved-output') {
    return fail('This group has no single output to connect from.')
  }

  if (plan.kind === 'rewire') {
    // A drop on a CONNECTED input row re-sources the hidden wires behind it.
    if (plan.underlyingEdgeIds.length === 0) return unchanged(graph)
    const ids = new Set(plan.underlyingEdgeIds)
    const moved = edges.filter((e) => ids.has(e.id))
    for (const edge of moved) {
      const verdict = canConnect(graph, {
        source: plan.source,
        sourceHandle: plan.sourceHandle,
        target: edge.target,
        targetHandle: edge.targetHandle,
      })
      if (!verdict.ok) return fail(verdict.reason)
    }
    const rewired = dropOtherUniverseEdges(
      nodes,
      edges.map((e) =>
        ids.has(e.id) ? { ...e, source: plan.source, sourceHandle: plan.sourceHandle } : e,
      ),
      plan.source,
    )
    const targets = new Set(moved.map((e) => e.target))
    const patched = nodes.map((n) => (targets.has(n.id) ? connectedTarget(n, nodes, rewired) : n))
    return { ok: true, changed: true, ...applyCollapseState(patched, rewired) }
  }

  let ends: WireEnds = conn
  if (plan.kind === 'add') {
    ends = {
      source: plan.source,
      sourceHandle: plan.sourceHandle,
      target: plan.target,
      targetHandle: plan.targetHandle,
    }
  } else {
    // A plain drag without a source handle takes the source's first output.
    const sourceNode = nodes.find((n) => n.id === conn.source)
    ends = {
      ...conn,
      sourceHandle:
        conn.sourceHandle ||
        UniversalNodeRegistry.getSchema(sourceNode?.type || '')?.outputs?.[0]?.id ||
        'output',
    }
  }
  const verdict = canConnect(graph, ends)
  if (!verdict.ok) return fail(verdict.reason)

  const kept = edges.filter((e) => !(isCanonical(e) && onSocket(e, ends.target, ends.targetHandle)))
  const added = addEdge(
    {
      source: ends.source,
      sourceHandle: ends.sourceHandle ?? null,
      target: ends.target,
      targetHandle: ends.targetHandle ?? null,
      ...(options.edgeType ? { type: options.edgeType } : {}),
    } as Connection,
    kept,
  )
  const nextEdges = dropOtherUniverseEdges(nodes, added, ends.source)
  const patched = nodes.map((n) =>
    n.id === ends.target ? connectedTarget(n, nodes, nextEdges) : n,
  )
  return { ok: true, changed: true, ...applyCollapseState(patched, nextEdges) }
}

// ── Disconnect ───────────────────────────────────────────────────────────────

/**
 * Remove wires and refresh the mirrors of the nodes they fed. With
 * `cascadeProxies` (a user delete), a collapsed subnet's proxy wire takes the
 * hidden wires it stands for and the projection is rebuilt; without it (React
 * Flow's own change stream, which collapse and expand also drive) a proxy
 * goes alone, so expanding a subnet never drops its connections.
 */
export function planDisconnect(
  graph: Graph,
  edgeIds: readonly string[],
  options: { cascadeProxies: boolean },
): PlanResult {
  const { nodes, edges } = graph
  const ids = new Set(edgeIds.filter((id) => edges.some((e) => e.id === id)))
  if (ids.size === 0) return unchanged(graph)
  let cascaded = false
  if (options.cascadeProxies) {
    for (const proxy of edges.filter((e) => ids.has(e.id) && isProxyEdge(e))) {
      for (const id of getUnderlyingEdgeIds(proxy, nodes, edges)) ids.add(id)
      cascaded = true
    }
  }
  const removed = edges.filter((e) => ids.has(e.id))
  const nextEdges = edges.filter((e) => !ids.has(e.id))
  const targets = new Set(removed.filter(isCanonical).map((e) => e.target))
  const patched = patchNodesAfterDisconnect(nodes, nextEdges, targets)
  if (!cascaded) return { ok: true, changed: true, nodes: patched, edges: nextEdges }
  return { ok: true, changed: true, ...applyCollapseState(patched, nextEdges) }
}

/** Remove whatever is wired into one input (the face's Disconnect). */
export function planDisconnectSocket(
  graph: Graph,
  target: string,
  targetHandle: string,
): PlanResult {
  const ids = graph.edges
    .filter((e) => isCanonical(e) && onSocket(e, target, targetHandle))
    .map((e) => e.id)
  return planDisconnect(graph, ids, { cascadeProxies: false })
}

// ── Reconnect ────────────────────────────────────────────────────────────────

/**
 * Move one end of a wire, as a fresh drag would place it: checked, replacing
 * what was on the new input, rebuilt from the STORED wire (React Flow hands
 * over its on-screen copy, whose display-only fields such as a greyed wire's
 * `disabled` would otherwise be saved), and with both inputs' mirrors
 * refreshed. A collapsed subnet's proxy wire moves the hidden wires it stands
 * for; its group end cannot be moved from outside.
 */
export function planReconnect(graph: Graph, oldEdge: Edge, conn: WireEnds): PlanResult {
  const { nodes, edges } = graph
  const boundary = resolveBoundaryReconnect(oldEdge, conn as Connection, nodes, edges)
  if (boundary.kind === 'blocked') {
    return fail("This end belongs to the group. Expand it to rewire what's inside.")
  }

  if (boundary.kind === 'rewire-through') {
    if (boundary.underlyingEdgeIds.length === 0) return unchanged(graph)
    const ids = new Set(boundary.underlyingEdgeIds)
    const rewired = edges.map((e) =>
      ids.has(e.id)
        ? {
            ...e,
            ...(boundary.newSource ?? {}),
            ...(boundary.newTarget ?? {}),
          }
        : e,
    )
    const others = { nodes, edges: edges.filter((e) => !ids.has(e.id)) }
    for (const edge of rewired.filter((e) => ids.has(e.id))) {
      const verdict = canConnect(others, edge)
      if (!verdict.ok) return fail(verdict.reason)
    }
    const before = edges.filter((e) => ids.has(e.id)).map((e) => e.target)
    const after = rewired.filter((e) => ids.has(e.id)).map((e) => e.target)
    const lost = new Set(before.filter((t) => !after.includes(t)))
    const gained = new Set(after)
    const patched = patchNodesAfterDisconnect(nodes, rewired, lost).map((n) =>
      gained.has(n.id) ? connectedTarget(n, nodes, rewired) : n,
    )
    return { ok: true, changed: true, ...applyCollapseState(patched, rewired) }
  }

  const stored = edges.find((e) => e.id === oldEdge.id) ?? oldEdge
  const others = { nodes, edges: edges.filter((e) => e.id !== stored.id) }
  const verdict = canConnect(others, conn)
  if (!verdict.ok) return fail(verdict.reason)

  // The old input's on/off flag stays behind: it belonged to that input.
  const { disabled: _disabled, ...data } = (stored.data ?? {}) as Record<string, unknown>
  const kept = edges.filter(
    (e) => e.id === stored.id || !(isCanonical(e) && onSocket(e, conn.target, conn.targetHandle)),
  )
  const moved = reconnectEdge({ ...stored, data }, conn as Connection, kept)
  const nextEdges = dropOtherUniverseEdges(nodes, moved, conn.source)
  const left = new Set(
    stored.target === conn.target && stored.targetHandle === conn.targetHandle
      ? []
      : [stored.target],
  )
  const patched = patchNodesAfterDisconnect(nodes, nextEdges, left).map((n) =>
    n.id === conn.target ? connectedTarget(n, nodes, nextEdges) : n,
  )
  return { ok: true, changed: true, ...applyCollapseState(patched, nextEdges) }
}

// ── Splice ───────────────────────────────────────────────────────────────────

/**
 * Wire changes as a delta, for callers whose event also changes nodes in
 * other ways (a drag re-parents the node, a drop adds it): apply it to the
 * CURRENT state with `applyDeltaToEdges` / `applyDeltaToNodes` instead of
 * replacing the state with a planned graph.
 */
export interface WireDelta {
  /** Wire ids that go. */
  remove: string[]
  /** Wires that come, already checked. */
  add: Edge[]
  /** Nodes that gained a wire: their mirror refreshes and their stamp clears. */
  connected: string[]
  /** Nodes that only lost a wire: their mirror refreshes. */
  disconnected: string[]
}

export type DeltaResult = { ok: true; delta: WireDelta } | { ok: false; reason: string }

/**
 * Put a node into the middle of a wire: source → the node's input, the node's
 * output → the wire's old target, each checked like a fresh drag (the far
 * side through a reroute is checked as what feeds it). Whatever else was on
 * either input is replaced. The three drops onto a wire (drag an unwired
 * node, drop a reroute, drop from the library) all use it; the caller picks
 * the handles (wire-insertion-matcher) and takes the undo snapshot.
 */
export function planSplice(
  graph: Graph,
  splice: { edgeId: string; nodeId: string; inputId: string; outputId: string },
  options: { edgeType?: string } = {},
): DeltaResult {
  const wire = graph.edges.find((e) => e.id === splice.edgeId)
  if (!wire) return { ok: false, reason: 'That wire is no longer on the canvas.' }
  if (isProxyEdge(wire)) {
    return { ok: false, reason: 'Expand the subnet to drop a node onto its wires.' }
  }
  const type = options.edgeType ? { type: options.edgeType } : {}
  const result = planWireChanges(
    graph,
    {
      remove: [wire.id],
      add: [
        {
          id: `edge-${wire.source}-${wire.sourceHandle}-${splice.nodeId}-${splice.inputId}`,
          source: wire.source,
          sourceHandle: wire.sourceHandle,
          target: splice.nodeId,
          targetHandle: splice.inputId,
          ...type,
        },
        {
          id: `edge-${splice.nodeId}-${splice.outputId}-${wire.target}-${wire.targetHandle}`,
          source: splice.nodeId,
          sourceHandle: splice.outputId,
          target: wire.target,
          targetHandle: wire.targetHandle,
          ...type,
        },
      ],
    },
    { occupied: 'replace' },
  )
  return result.ok ? { ok: true, delta: result.delta } : result
}

export type WireChangeResult =
  | { ok: true; delta: WireDelta; dropped: Array<{ wire: Edge; reason: string }> }
  | { ok: false; reason: string }

/**
 * Remove some wires and add others, in one step. Each added wire is checked
 * like a drag against the graph with the removals and the wires added before
 * it, so a wire out of a new reroute is checked as what the reroute is fed.
 * A collapsed subnet's projection (proxy) wire goes through unchecked: it
 * stands for wires that are checked themselves.
 *
 * `occupied` says what to do when an added wire's input already has a wire:
 * `replace` (a user's own wiring: drop onto a node, the slice tool) takes it,
 * and one refused wire refuses the whole change; `skip` (paste, duplicate)
 * leaves that wire out, never stacking two wires on an input or taking
 * another node's wire, and also leaves out refused wires, reporting both in
 * `dropped`.
 */
export function planWireChanges(
  graph: Graph,
  change: { remove?: readonly string[]; add: readonly Edge[] },
  options: { occupied: 'replace' | 'skip' },
): WireChangeResult {
  const { nodes, edges } = graph
  const remove = new Set((change.remove ?? []).filter((id) => edges.some((e) => e.id === id)))
  let working = edges.filter((e) => !remove.has(e.id))
  const added: Edge[] = []
  const dropped: Array<{ wire: Edge; reason: string }> = []

  for (const wire of change.add) {
    if (isProxyEdge(wire)) {
      added.push(wire)
      working = [...working, wire]
      continue
    }
    const held = working.filter(
      (e) => isCanonical(e) && onSocket(e, wire.target, wire.targetHandle),
    )
    if (held.length > 0 && options.occupied === 'skip') {
      dropped.push({ wire, reason: 'That input already has a wire.' })
      continue
    }
    const verdict = canConnect({ nodes, edges: working.filter((e) => !held.includes(e)) }, wire)
    if (!verdict.ok) {
      if (options.occupied === 'replace') return verdict
      dropped.push({ wire, reason: verdict.reason })
      continue
    }
    for (const e of held) remove.add(e.id)
    working = [...working.filter((e) => !held.includes(e)), wire]
    added.push(wire)
  }

  const removedEdges = edges.filter((e) => remove.has(e.id))
  const connected = [...new Set(added.filter(isCanonical).map((e) => e.target))]
  const disconnected = [
    ...new Set(
      removedEdges
        .filter(isCanonical)
        .map((e) => e.target)
        .filter((t) => !connected.includes(t)),
    ),
  ]
  return { ok: true, delta: { remove: [...remove], add: added, connected, disconnected }, dropped }
}

/** `edges` after a delta. */
export function applyDeltaToEdges(edges: Edge[], delta: WireDelta): Edge[] {
  const gone = new Set(delta.remove)
  return [...edges.filter((e) => !gone.has(e.id)), ...delta.add]
}

/** `nodes` after a delta (with `edges` the wires after it): mirrors and stamps. */
export function applyDeltaToNodes(nodes: Node[], edges: Edge[], delta: WireDelta): Node[] {
  const connected = new Set(delta.connected)
  const disconnected = new Set(delta.disconnected)
  if (connected.size === 0 && disconnected.size === 0) return nodes
  return nodes.map((n) =>
    connected.has(n.id)
      ? connectedTarget(n, nodes, edges)
      : disconnected.has(n.id)
        ? patchInputsOnDisconnect(n, nodes, edges)
        : n,
  )
}

// ── A setting change ─────────────────────────────────────────────────────────

/** What a setting change does to the node's wires, inputs and subnet. */
export interface ParamPlan {
  /** Wires the change lets go of, and the node's mirror refresh. */
  delta: WireDelta
  /** The node's input entries after the change, when they change. */
  inputs?: NodeInput[]
  /** Subnets that stop exposing an input the change wires again. */
  exposure: Array<{ subnetId: string; exposure: SubnetExposure }>
}

/** `parameters` with one setting changed, in its stored shape (record or list). */
function withParameter(parameters: unknown, key: string, value: unknown): unknown {
  if (Array.isArray(parameters)) {
    const named = (p: unknown) => (p as { name?: string } | null)?.name === key
    return parameters.some(named)
      ? parameters.map((p) => (named(p) ? { ...(p as object), value } : p))
      : [...parameters, { name: key, value }]
  }
  return { ...((parameters as Record<string, unknown> | undefined) ?? {}), [key]: value }
}

/**
 * What changing one setting does beyond the value itself, worked out once for
 * every way a setting changes: the node face, the pinned card, a subnet's
 * exposed settings and the AI (step 4 of the connection-logic audit; it used
 * to live in the node face, which the others skipped). The caller writes the
 * value in its own way, then applies this.
 *
 * - Node or Static for an input (a Condition's B): the input's entry is
 *   rewritten (the typed number kept for later); Static lets go of its wire,
 *   and Node drops the typed number from a subnet that exposed it.
 * - A face that opts in (`releaseInactive`: Trade Manager, Entry Rules,
 *   Target, Stop): a socket the new mode takes out of play lets go of its wire
 *   and entry, and one it brings into play gets an entry to record its field.
 * - Any other wire into the node that no longer fits (a Target that now reads
 *   a true/false) lets go too.
 */
export function planParamChange(
  graph: Graph,
  nodeId: string,
  key: string,
  value: unknown,
): ParamPlan {
  const none: ParamPlan = {
    delta: { remove: [], add: [], connected: [], disconnected: [] },
    exposure: [],
  }
  const { nodes, edges } = graph
  const node = nodes.find((n) => n.id === nodeId)
  const schema = node ? UniversalNodeRegistry.getSchema(node.type ?? '') : undefined
  if (!node || !schema || key === 'inputs') return none

  const before = node.data?.parameters
  const after = withParameter(before, key, value)
  const current = inputsOf(node) ?? []
  let inputs: NodeInput[] | undefined
  let exposure: ParamPlan['exposure'] = []
  const release = new Set<string>()

  const union = schema.inputs.find((i) => i.discriminatedUnion?.parameterName === key)
  // Node, then Static, then Node again brings back the wire it had (Chris,
  // 2026-10-06), as Static brings back the number typed before: the wire a
  // switch to Static removes is parked on the input entry.
  let restore: Edge | null = null
  if (union && (value === 'node' || value === 'static')) {
    inputs = inputsForTypeSwitch(current, union, value)
    const entry = current.find((input) => input?.name === union.id)
    if (value === 'static') {
      release.add(union.id)
      const wire = edges.find(
        (e) => isCanonical(e) && e.target === nodeId && e.targetHandle === union.id,
      )
      if (wire) {
        const parked = {
          source: wire.source,
          sourceHandle: wire.sourceHandle ?? null,
          ...(entry?.field ? { field: entry.field } : {}),
        }
        inputs = inputs.map((input) =>
          input?.name === union.id ? { ...input, _parkedWire: parked } : input,
        )
      }
    } else {
      const parked = entry?._parkedWire
      if (parked && nodes.some((n) => n.id === parked.source)) {
        restore = {
          id: `edge-${parked.source}-${parked.sourceHandle}-${nodeId}-${union.id}`,
          source: parked.source,
          sourceHandle: parked.sourceHandle,
          target: nodeId,
          targetHandle: union.id,
          type: 'editable-edge',
        }
        if (parked.field) {
          inputs = inputs.map((input) =>
            input?.name === union.id ? { ...input, field: parked.field } : input,
          )
        }
      }
      exposure = nodes
        .filter((n) => n.type === 'subnet')
        .flatMap((sub) => {
          const exposed = getExposure(sub.data)
          const hit = exposed.items.some(
            (item) =>
              item.kind === 'param' &&
              item.nodeId === nodeId &&
              item.paramName === union.id &&
              item.source === 'input',
          )
          return hit
            ? [
                {
                  subnetId: sub.id,
                  exposure: removeExposedParam(exposed, nodeId, union.id, 'input'),
                },
              ]
            : []
        })
    }
  }

  if (resolveFace(schema).releaseInactive) {
    const outOfPlay = new Set(inputsReleasedBy(schema, key, value, before))
    outOfPlay.forEach((id) => release.add(id))
    let next = inputs ?? current
    if (next.some((input) => outOfPlay.has(input?.name ?? ''))) {
      next = next.filter((input) => !outOfPlay.has(input?.name ?? ''))
    }
    for (const input of schema.inputs) {
      const comesIntoPlay =
        isKeyedTo(input, key) &&
        !isInputActive(schema, input, before) &&
        isInputActive(schema, input, after)
      if (comesIntoPlay && MIRRORED.has(schema.type) && !next.some((i) => i?.name === input.id)) {
        next = [...next, { name: input.id, inputType: 'node', source: null }]
      }
    }
    if (next !== current) inputs = next
  }

  const into = edges.filter((e) => isCanonical(e) && e.target === nodeId)
  const remove = into.filter((e) => release.has(e.targetHandle ?? '')).map((e) => e.id)
  const changed = { ...node, data: { ...node.data, parameters: after } }
  const graphAfter = {
    nodes: nodes.map((n) => (n.id === nodeId ? changed : n)),
    edges: edges.filter((e) => !remove.includes(e.id)),
  }
  for (const wire of into) {
    if (remove.includes(wire.id)) continue
    if (!canConnect(graphAfter, wire).ok) remove.push(wire.id)
  }
  // The parked wire goes back only where it still fits.
  const occupied =
    restore !== null &&
    graphAfter.edges.some(
      (e) => isCanonical(e) && e.target === nodeId && e.targetHandle === restore?.targetHandle,
    )
  const add = restore && !occupied && canConnect(graphAfter, restore).ok ? [restore] : []

  return {
    delta: {
      remove,
      add,
      connected: add.length > 0 ? [nodeId] : [],
      disconnected: remove.length > 0 ? [nodeId] : [],
    },
    ...(inputs ? { inputs } : {}),
    exposure,
  }
}

// ── The input section's requests ─────────────────────────────────────────────

export type InputPlanResult =
  | { ok: true; delta: WireDelta; inputs?: NodeInput[] }
  | { ok: false; reason: string }

const noWires: WireDelta = { remove: [], add: [], connected: [], disconnected: [] }

/** The node's input entries with one entry patched (added when missing). */
function patchEntry(node: Node, inputId: string, patch: Partial<NodeInput>): NodeInput[] {
  const inputs = [...(inputsOf(node) ?? [])]
  const index = inputs.findIndex((i) => i?.name === inputId)
  inputs[index >= 0 ? index : inputs.length] = {
    ...(index >= 0 ? inputs[index] : { name: inputId }),
    ...patch,
  } as NodeInput
  return inputs
}

/**
 * Pick the line an input reads (a Stochastic's %K or %D, a bar's High). From
 * a price bar the line is a field the input records. Otherwise the wire is
 * moved to that output where it leaves its source: through a reroute or a
 * subnet pill that is the wire into the pass-through (which carries one
 * line), since rewriting the pass-through's own wire detached it (audit item
 * 8). Checked like a drag.
 */
export function planSelectLine(
  graph: Graph,
  nodeId: string,
  inputId: string,
  line: string,
): InputPlanResult {
  const { nodes, edges } = graph
  const node = nodes.find((n) => n.id === nodeId)
  if (!node) return { ok: false, reason: 'That node is no longer on the canvas.' }
  const wire = edges.find((e) => isCanonical(e) && onSocket(e, nodeId, inputId))
  let feed = wire
  for (let hops = 0; feed && hops < 50; hops++) {
    const from = nodes.find((n) => n.id === feed!.source)
    if (!isPassthroughType(from?.type)) break
    const up = edges.find((e) => isCanonical(e) && e.target === from!.id)
    if (!up) break
    feed = up
  }
  const source = feed ? nodes.find((n) => n.id === feed!.source) : undefined
  if (!wire || !feed || !source || source.type === 'priceData' || source.type === 'marketData') {
    const field =
      source?.type === 'priceData' || source?.type === 'marketData' ? { field: line } : {}
    return {
      ok: true,
      delta: noWires,
      inputs: patchEntry(node, inputId, { source: line, ...field }),
    }
  }
  if (feed.sourceHandle === line) return { ok: true, delta: noWires }
  const moved: Edge = { ...feed, sourceHandle: line }
  const verdict = canConnect(
    { nodes, edges: edges.map((e) => (e.id === feed!.id ? moved : e)) },
    wire,
  )
  if (!verdict.ok) return verdict
  return {
    ok: true,
    delta: { remove: [feed.id], add: [moved], connected: [nodeId], disconnected: [] },
  }
}

/**
 * Switch an input on or off (an And/Or Gate's rows): its entry only. The
 * off-branch pruning and the canvas read the entry (audit step 8).
 */
export function planInputEnabled(
  graph: Graph,
  nodeId: string,
  inputId: string,
  enabled: boolean,
): { delta: WireDelta; inputs?: NodeInput[] } {
  const node = graph.nodes.find((n) => n.id === nodeId)
  if (!node) return { delta: noWires }
  // The entry is the one store (connection audit step 8): the wire is drawn
  // off from it at render time, never stamped.
  return { delta: noWires, inputs: patchEntry(node, inputId, { enabled }) }
}
